'use client';

import type React from 'react';
import { Button } from '@/app/components/ui/button';
import { BREAKPOINTS } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { GenerateNowText, TOOL_INFO } from '../constants';
import { type HeadVideoProps, TOOL_TYPE } from '../types';
import styles from './index.module.scss';

function Video({
  poster,
  mobilePoster,
  url,
  clsName,
  mobileUrl,
  alt = 'AI generated video',
}: {
  poster: string;
  mobilePoster?: string;
  url: string;
  clsName?: string;
  /** 与 VideoLazyLoad：max-width SM_MAX 时使用；不传则全宽用 url */
  mobileUrl?: string;
  alt?: string;
}) {
  const hasMobileVideoSource = mobileUrl !== undefined;
  const shouldRenderMobileVideo = Boolean(mobileUrl);
  const desktopSourceMedia = hasMobileVideoSource ? `(min-width: ${BREAKPOINTS.MD_MIN}px)` : undefined;
  const mobileSourceMedia = `(max-width: ${BREAKPOINTS.SM_MAX}px)`;

  return (
    <>
      {/* 与 VideoLazyLoad 一致：picture + source media 与下方 video source 断点对齐 */}
      <picture className={cn(clsName)}>
        {mobilePoster ? <source media={mobileSourceMedia} srcSet={mobilePoster} /> : null}
        <img src={poster} alt={alt} className="h-full w-full object-cover" decoding="async" />
      </picture>
      <video muted autoPlay loop playsInline webkit-playsinline="true" x5-playsinline="true" className={cn(clsName)}>
        {shouldRenderMobileVideo ? <source media={mobileSourceMedia} src={mobileUrl} type="video/mp4" /> : null}
        <source media={desktopSourceMedia} src={url} type="video/mp4" />
        <track kind="captions" />
      </video>
    </>
  );
}

export const HeadVideo: React.FC<HeadVideoProps> = ({ toolKey, toolConfig, clsName }) => {
  const router = useRouter();
  const productInfo = TOOL_INFO[toolKey];

  const handleGenerateNowClick = () => {
    reportEvent('productpage_generate', {
      element_name: `${toolKey} _generate`,
    });
    router.push(toolConfig.link);
  };

  return [
    TOOL_TYPE.AI_KISSING_GENERATOR,
    TOOL_TYPE.AI_HUG_GENERATOR,
    TOOL_TYPE.AI_DANCE_GENERATOR,
    TOOL_TYPE.AI_TIKTOK_VIDEO_GENERATOR,
    TOOL_TYPE.AI_FIGHT_GENERATOR,
    TOOL_TYPE.AI_REEL_GENERATOR,
    TOOL_TYPE.AI_MEME_GENERATOR,
  ].includes(toolKey) ? (
    <section className={cn('w-full overflow-hidden', 'bg-[linear-gradient(90deg,#e0ddff4d_0%,#bcd8ff4d_100%)]')}>
      <div
        className={cn(
          'max-w-[1920px]',
          'mx-auto flex-row gap-[54px] px-[100px] py-[91px] md:min-h-[520px] md:flex-col md:gap-0 md:px-4 md:py-[40px]',
          clsName,
        )}
      >
        <div className="flex-1 md:mt-8 md:text-center">
          <h1 className="mb-3 font-bold text-[56px] text-black leading-[1.4] md:mb-3 md:text-[32px]">
            {productInfo.title}
          </h1>
          <p className="mb-10 max-w-3xl text-base text-black leading-[1.6] md:mb-8">{productInfo.description}</p>
          <Button variant="primary" size="xl" onClick={handleGenerateNowClick}>
            {GenerateNowText}
          </Button>
        </div>
        {/* md:flex-col + parent clsName `items-center` shrinks cross-axis size; stretch + full width keeps aspect-ratio box from collapsing */}
        <div className="flex min-w-0 flex-1 justify-center md:order-first md:w-full md:max-w-full md:self-stretch">
          <div className="relative w-full min-w-0 flex-1">
            <div className="relative w-full">
              <div className="pointer-events-none absolute bottom-[-42px] left-[-105px] h-[239px] w-[239px] bg-[#94B2FF] opacity-80 blur-[90px]" />
              <div className="pointer-events-none absolute top-0 right-0 z-[1] h-[170px] w-[170px] bg-[#C291FF] blur-[75px]" />
              {/* Fixed aspect avoids CLS when <video> intrinsic size resolves (matches generate section ~546×307). */}
              <div className="relative z-[2] aspect-video w-full overflow-hidden rounded-[32px] md:rounded-[18px]">
                {toolConfig.image ? (
                  <img
                    src={toolConfig.image.mobileSrc ?? toolConfig.image.src}
                    alt={productInfo.title}
                    className="absolute inset-0 h-full w-full object-cover md:hidden"
                  />
                ) : null}
                {toolConfig.image ? (
                  <img
                    src={toolConfig.image.src}
                    alt={productInfo.title}
                    className="absolute inset-0 hidden h-full w-full object-cover md:block"
                  />
                ) : null}
                {toolConfig.video ? (
                  <Video
                    poster={toolConfig.video.poster}
                    mobilePoster={toolConfig.video.mobilePoster}
                    url={toolConfig.video.url}
                    mobileUrl={toolConfig.video.mobileUrl}
                    clsName="absolute inset-0 h-full w-full object-cover"
                    alt={productInfo.title}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  ) : (
    <div className="relative w-full overflow-hidden">
      <Video
        poster={toolConfig.video?.poster ?? ''}
        mobilePoster={toolConfig.video?.mobilePoster}
        url={toolConfig.video?.url ?? ''}
        mobileUrl={toolConfig.video?.mobileUrl}
        clsName="absolute top-0 left-0 hidden h-full w-full object-cover opacity-0 lg:h-full opacity-100 transition-opacity duration-150 ease-in-out blur blur-3xl 2xl:block aspect-[144/52]"
        alt={productInfo.title}
      />
      <div className="mx-auto max-w-[1920px]">
        <div
          className={cn(
            'relative z-10 aspect-[144/52] min-h-[590px] max-w-full flex-col text-center md:min-h-[428px]',
            clsName,
          )}
        >
          <h1
            className="mb-3 font-bold text-[56px] text-white leading-[1.4] md:mb-6 md:text-[32px]"
            style={{
              textShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)',
            }}
          >
            {productInfo.title}
          </h1>
          <p
            className="mx-auto mb-10 max-w-3xl text-base text-white leading-[1.6] md:hidden"
            style={{
              textShadow: '0px 4px 6px rgba(0, 0, 0, 0.25)',
            }}
          >
            {productInfo.description}
          </p>
          <Button variant="primary" size="xl" onClick={handleGenerateNowClick}>
            {GenerateNowText}
          </Button>
          <div className={cn('-z-20 absolute top-0 right-0 bottom-0 left-0', styles.haadVideo)}>
            <Video
              poster={toolConfig.video?.poster ?? ''}
              mobilePoster={toolConfig.video?.mobilePoster}
              url={toolConfig.video?.url ?? ''}
              mobileUrl={toolConfig.video?.mobileUrl}
              clsName="hidden w-full h-full object-cover opacity-0 lg:h-full block opacity-100 transition-opacity duration-150 ease-in-out aspect-[144/52]"
              alt={productInfo.title}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
